import React, { useState, useRef, useEffect } from "react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const LEAVE_OPTIONS = [
  { id: "annual_leave", name: "Annual Leave" },
  { id: "bereavement_leave", name: "Bereavement (Compassionate) Leave" },
  { id: "community_service_leave", name: "Community Service Leave" },
  { id: "long_service_leave", name: "Long Service Leave" },
  { id: "other_paid_leave", name: "Other Paid Leave" },
  { id: "sick_leave", name: "Sick (Personal/Carer's) Leave" },
  { id: "time_off_in_lieu", name: "Time Off In Lieu" },
  { id: "unpaid_leave_leave", name: "Unpaid Leave - Leave" },
  { id: "unpaid_leave_no_show", name: "Unpaid Leave - No Show" },
  { id: "unpaid_leave_sick", name: "Unpaid Leave - Sick" },
];

export default function LeaveAddComponent({
  value = [],
  onChange,
  disabled = false,
}) {
  const [dropupOpen, setDropupOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState([]);
  const dropupRef = useRef(null);

  // Close dropup when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (dropupRef.current && !dropupRef.current.contains(e.target)) {
        setDropupOpen(false);
        setSearch("");
        setPending([]);
      }
    }
    if (dropupOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropupOpen]);

  // Filter leave options
  const filteredLeaves = LEAVE_OPTIONS.filter(
    (leave) =>
      leave.name.toLowerCase().includes(search.toLowerCase()) &&
      !value.some((s) => s.id === leave.id)
  );

  // Select all filtered leaves
  const handleSelectAll = () => {
    setPending(filteredLeaves.map((l) => l.id));
  };

  // Toggle pending selection
  const togglePending = (id) => {
    setPending((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  // Add selected leaves
  const handleAdd = () => {
    const newLeaves = LEAVE_OPTIONS.filter((leave) =>
      pending.includes(leave.id)
    );
    const updated = [...value, ...newLeaves];
    onChange && onChange(updated);
    setDropupOpen(false);
    setSearch("");
    setPending([]);
  };

  // Remove selected leave
  const handleRemove = (id) => {
    const updated = value.filter((leave) => leave.id !== id);
    onChange && onChange(updated);
  };

  return (
    <div className="w-full">
      {/* Add leave button */}
      <div className="relative flex flex-col items-start" ref={dropupRef}>
        <button
          type="button"
          className="text-gray-700 font-medium hover:underline focus:outline-none"
          onClick={() => setDropupOpen((open) => !open)}
          disabled={disabled}
        >
          Add leave
        </button>
        {/* Dropup */}
        {dropupOpen && (
          <div className="absolute bottom-10 left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 animate-fade-in-up">
            {/* Search */}
            <div className="flex items-center px-3 py-2 border-b">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2" />
              <input
                type="text"
                className="w-full text-sm outline-none border-none bg-transparent"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={disabled}
              />
            </div>
            {/* Select all */}
            <button
              type="button"
              className="block w-full text-left text-gray-700 px-3 py-2 text-sm hover:bg-gray-50"
              onClick={handleSelectAll}
              disabled={filteredLeaves.length === 0 || disabled}
            >
              Select all leaves ({filteredLeaves.length})
            </button>
            {/* List */}
            <div className="max-h-40 overflow-y-auto">
              {filteredLeaves.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">
                  No leaves found
                </div>
              )}
              {filteredLeaves.map((leave) => (
                <label
                  key={leave.id}
                  className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50"
                  title={leave.name}
                >
                  <input
                    type="checkbox"
                    className="accent-gray-600 mr-2"
                    checked={pending.includes(leave.id)}
                    onChange={() => togglePending(leave.id)}
                    disabled={disabled}
                  />
                  <span className="text-gray-800 text-sm">{leave.name}</span>
                </label>
              ))}
            </div>
            {/* Add button */}
            <button
              type="button"
              className={`w-full px-4 py-2 mt-2 mb-2 rounded-md font-medium text-sm transition
                ${
                  pending.length === 0 || disabled
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-700 text-white hover:bg-gray-800"
                }
              `}
              onClick={handleAdd}
              disabled={pending.length === 0 || disabled}
            >
              Add leave{pending.length ? ` (${pending.length})` : ""}
            </button>
          </div>
        )}
      </div>
      {/* Selected leaves */}
      <div className="mt-4 space-y-2">
        {value.map((leave) => (
          <div
            key={leave.id}
            className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded"
          >
            <span className="text-gray-800 text-sm">{leave.name}</span>
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-red-600"
              onClick={() => handleRemove(leave.id)}
              aria-label="Remove"
              disabled={disabled}
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
